import { animate, motion, useMotionValue, useTransform } from "framer-motion";
import { PropsWithChildren, useEffect } from "react";

type BorderLoaderAnimationProps = PropsWithChildren<{
    isActive: boolean;
    isCompleting?: boolean;
    className?: string;
}>;

const defaultFillPercentage = 25;
const defaultRotationAngle = 45;

export const BorderLoaderAnimation = ({ isActive, isCompleting = false, className = "" }: BorderLoaderAnimationProps) => {
    const rotationAngle = useMotionValue(defaultRotationAngle);
    const fillPercentage = useMotionValue(defaultFillPercentage); // Controls how much of the circle is filled
    const rounded = useTransform(rotationAngle, (latest) => Math.round(latest));

    const usefulValue = useTransform(
        [rounded, fillPercentage],
        ([rotation, fill]: number[]) => {
            if (!isActive && !isCompleting) {
                return '#EEEEEE';
            }
            return `conic-gradient(from ${rotation}deg, #EEEEEE 0% ${100 - fill}%, #000000 ${100 - fill}% 100%)`
        }
    );

    useEffect(() => {
        let rotationControls: any;
        let fillControls: any;

        if (isCompleting) {
            // Stop any existing rotation animation
            if (rotationControls) rotationControls.stop();

            // Get current rotation value and continue from there
            const currentRotation = rotationAngle.get();

            // Animate to fill the entire circle
            fillControls = animate(fillPercentage, 100, {
                duration: 0.8,
                ease: "easeOut"
            });

            // Continue rotating clockwise from current position for the filling effect
            rotationControls = animate(rotationAngle, currentRotation + 360, {
                duration: 0.8,
                ease: "easeOut"
            });

            return () => {
                if (rotationControls) rotationControls.stop();
                if (fillControls) fillControls.stop();
            };

        } else if (isActive) {
            // Reset fill percentage for spinning animation
            fillPercentage.set(defaultFillPercentage);

            // Start infinite rotation
            rotationControls = animate(rotationAngle, defaultRotationAngle + 360, {
                duration: 1,
                ease: "linear",
                repeat: Infinity,
                repeatType: "loop"
            });

            return () => {
                if (rotationControls) rotationControls.stop();
            };
        } else {
            // Reset to initial state when not active
            rotationAngle.set(defaultRotationAngle);
            fillPercentage.set(defaultFillPercentage);
        }
    }, [isActive, isCompleting, rotationAngle, fillPercentage]);

    return (
        <motion.div
            className={`
            absolute
            top-1/2
            left-1/2
            transform
            -translate-x-1/2
            -translate-y-1/2
            z-[-1]
            ${className}
        `}
            style={{
                background: usefulValue,
                width: "calc(100% + 4px)",
                height: "calc(100% + 4px)",
            }}
        />
    );
};